<template>
  <h1>SurveyTableDev</h1>

  <div>
    <button class="btn btn-dark" @click="expandAllGroups">
      Expand all groups
    </button>
    <button class="btn btn-dark ms-2" @click="contractAllGroups">
      Contract all groups
    </button>
  </div>

  <div
    v-for="questionGroup in questionGroups" :key="questionGroup.index"
    class="question-group mb-4"
  >
    <h2>{{ questionGroup.identifier }} - {{ questionGroup.title }}</h2>

    <div>
      <button class="btn btn-dark" @click="expandGroup(questionGroup)">
        Expand group
      </button>
      <button class="btn btn-dark ms-2" @click="contractGroup(questionGroup)">
        Contract group
      </button>
    </div>

    <p>
      <strong>Description</strong>: {{ questionGroup.description }}<br>
      <strong>Use counter</strong>: {{ questionGroup.useCounter ? 'TRUE' : 'FALSE' }}<br>
      <strong>Index</strong>: {{ questionGroup.index }}<br>
      <strong>Question count</strong>:
      {{ questionGroup.questions ? questionGroup.questions.length : 0 }}
    </p>

    <div v-if="questionGroupIdsExpanded[questionGroup.id]">
      <div>
        <button class="btn btn-dark" @click="expandAllQuestionsInGroup(questionGroup)">
          Expand all questions in group
        </button>
        <button class="btn btn-dark ms-2" @click="contractAllQuestionsInGroup(questionGroup)">
          Contract all questions in group
        </button>
      </div>

      <table class="table table-bordered table-striped table-sm table-responsive">
        <thead>
          <tr>
            <th>ID</th>
            <th>Index</th>
            <th>Identifier</th>
            <th>Question</th>
            <th>Type</th>
            <th>Hint</th>
            <th>Dependency</th>
            <th>Answers</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="question in questionGroup.questions" :key="question.id">
            <td>{{ question.id }}</td>
            <td>{{ question.index }}</td>
            <td>{{ question.identifier }}</td>
            <td><markdown-text>{{ question.question }}</markdown-text></td>
            <td>{{ question.type }}</td>
            <td>
              <div v-if="question.hint">
                <markdown-text>{{ question.hint }}</markdown-text>
              </div>
              <div v-else class="no">
                (no)
              </div>
            </td>
            <td>
              <div v-if="question.dependency" class="dependency">
                <span
                  class="with-title"
                  :title="getQuestionText(question.dependency.groupIdentifier, question.dependency.questionIdentifier)"
                >
                  {{ question.dependency.groupIdentifier }}.{{ question.dependency.questionIdentifier }}
                </span>
                <span class="equal-sign ms-1 me-1">{{
                  question.dependency.negate ? '!=' : '=='
                }}</span>
                <span class="answer-container">
                  <span
                    v-for="(answerIdentifier, n) in question.dependency.answerIdentifiers"
                    :key="answerIdentifier" class="answer"
                  >
                    <span
                      :title="getAnswerText(question.dependency.groupIdentifier, question.dependency.questionIdentifier, answerIdentifier)"
                      class="with-title"
                    >
                      {{ decToRoman(answerIdentifier) }}
                    </span>
                    <span
                      v-if="n < question.dependency.answerIdentifiers.length - 1"
                      class="comma ms-1 me-1"
                    >,</span>
                  </span>
                </span>
              </div>
              <div v-else class="no">
                (no)
              </div>
            </td>
            <td>
              <div v-if="questionIdsExpanded[question.id] && question.answers instanceof Array && question.answers.length > 0">
                <button
                  class="btn btn-sm btn-dark"
                  @click.prevent.stop="contractQuestionAnswers(question.id)"
                >
                  Contract
                </button>

                <table class="table table-bordered table-striped table-sm">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Identifier</th>
                      <th>Answer</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="answer in question.answers" :key="answer.id">
                      <td>{{ answer.id }}</td>
                      <td>
                        <span
                          :title="answer.identifier"
                          class="with-title"
                        >{{ decToRoman(answer.identifier) }}</span>
                      </td>
                      <td><markdown-text>{{ answer.answer }}</markdown-text></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else-if="question.answers">
                <span v-if="typeof (question.answers) === 'string'" class="with-title" title="The options to this are based on a dynamic list">
                  {{ question.answers }}
                </span>
                <button
                  v-else
                  class="btn btn-sm btn-dark"
                  @click.prevent.stop="expandQuestionAnswers(question.id)"
                >
                  Expand ({{ question.answers ? question.answers.length : 0 }} answers)
                </button>
              </div>
              <div v-else>
                <span class="with-title" title="The type 'message' does not have any answers">
                  (none)
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>

import { mapState } from 'pinia';
import { useSurveyStore } from '@/store';
import { numberToRomanNumerals } from '@/utils/romanNumeralUtil';
import DevComponentContainer from './DevComponentContainer.vue';
import EventTicksComponentMixin from './EventTicksComponentMixin';
import MarkdownText from '@/components/common/MarkdownText';

export default {
  name: 'SurveyTableDev',
  components: { MarkdownText },
  extends: DevComponentContainer,
  mixins: [EventTicksComponentMixin],
  computed: {
    ...mapState(useSurveyStore, ['questionGroups']),
  },
  // eslint-disable-next-line vue/order-in-components
  data() {
    return {
      questionGroupIdsExpanded: {},
      questionIdsExpanded: {},
      events: [],
    };
  },
  watch: {
    questionGroups: {
      handler() {
        this.recalculateQuestionIdsExpanded();
      },
      deep: true,
    },
  },
  mounted() {
    this.recalculateQuestionIdsExpanded();
  },
  methods: {
    decToRoman(dec) {
      return numberToRomanNumerals(dec).toLowerCase();
    },
    hasQuestionIdKey(questionId) {
      return Object.keys(this.questionIdsExpanded).includes(questionId.toString());
    },
    getQuestionText(questionGroupIdentifier, questionIdentifier) {
      for (const questionGroup of this.questionGroups) {
        if (questionGroup.identifier.toString() !== questionGroupIdentifier.toString()) {
          continue;
        }

        for (const question of questionGroup.questions) {
          if (question.identifier.toString() !== questionIdentifier.toString()) {
            continue;
          }

          return question.question;
        }
      }

      return null;
    },
    getAnswerText(questionGroupIdentifier, questionIdentifier, answerIdentifier) {
      for (const questionGroup of this.questionGroups) {
        if (questionGroup.identifier.toString() !== questionGroupIdentifier.toString()) {
          continue;
        }

        for (const question of questionGroup.questions) {
          if (question.identifier.toString() !== questionIdentifier.toString()) {
            continue;
          }

          for (const answer of question.answers) {
            if (answer.identifier.toString() !== answerIdentifier.toString()) {
              continue;
            }

            return answer.answer;
          }
        }
      }

      return null;
    },
    expandAllGroups() {
      for (const questionGroup of this.questionGroups) {
        this.questionGroupIdsExpanded[questionGroup.id.toString()] = true;
      }
    },
    contractAllGroups() {
      for (const questionGroup of this.questionGroups) {
        this.questionGroupIdsExpanded[questionGroup.id.toString()] = false;
      }
    },
    expandAllQuestions() {
      for (const questionGroup of this.questionGroups) {
        for (const question of questionGroup.questions) {
          this.questionIdsExpanded[question.id.toString()] = true;
        }
      }
    },
    contractAllQuestions() {
      for (const questionGroup of this.questionGroups) {
        for (const question of questionGroup.questions) {
          this.questionIdsExpanded[question.id.toString()] = false;
        }
      }
    },
    expandGroup(questionGroup) {
      this.questionGroupIdsExpanded[questionGroup.id] = true;
    },
    contractGroup(questionGroup) {
      this.questionGroupIdsExpanded[questionGroup.id] = false;
    },
    expandAllQuestionsInGroup(questionGroup) {
      for (const question of questionGroup.questions) {
        this.expandQuestionAnswers(question.id);
      }
    },
    contractAllQuestionsInGroup(questionGroup) {
      for (const question of questionGroup.questions) {
        this.contractQuestionAnswers(question.id);
      }
    },
    expandQuestionAnswers(questionId) {
      console.log('questionId', questionId);
      if (!this.hasQuestionIdKey(questionId)) {
        return;
      }

      this.questionIdsExpanded[questionId] = true;
    },
    contractQuestionAnswers(questionId) {
      if (!this.hasQuestionIdKey(questionId)) {
        return;
      }

      this.questionIdsExpanded[questionId] = false;
    },
    recalculateQuestionIdsExpanded() {
      const questionIdsExpanded = {};
      const questionGroupIdsExpanded = {};

      for (const questionGroup of this.questionGroups) {
        questionGroupIdsExpanded[questionGroup.id.toString()] = false;

        for (const question of questionGroup.questions) {
          questionIdsExpanded[question.id.toString()] = true;
        }
      }

      this.questionIdsExpanded = questionIdsExpanded;
      this.questionGroupIdsExpanded = questionGroupIdsExpanded;
    },
  },
};
</script>

<style lang="scss" scoped>
.no {
  font-style: italic;
  font-size: 12px;
  color: #cccccc;
}

.with-title {
  border-bottom: 1px dashed #888888;

  &:hover {
    border-bottom: 3px solid #0000ff;
    cursor: help;
  }
}

.answer-container {
  .answer {
    &.last-child {
      .comma {
        display: none;
      }
    }
  }
}
</style>
